import type { ApiPoint, LatLng } from '@/types/api';

/** GeoJSON is [lng, lat]. Every conversion in the app goes through here. */
export function fromPoint(point?: ApiPoint | null): LatLng | null {
  if (!point?.coordinates || point.coordinates.length !== 2) return null;
  const [lng, lat] = point.coordinates;
  return { lat, lng };
}

export function toPoint({ lat, lng }: LatLng): ApiPoint {
  return { type: 'Point', coordinates: [lng, lat] };
}

export function toLngLat({ lat, lng }: LatLng): [number, number] {
  return [lng, lat];
}

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (deg: number) => (deg * Math.PI) / 180;

export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/**
 * Circle geofences are stored as a centre + radius, but MapLibre only fills
 * polygons — so a circle becomes a 64-gon at render time.
 */
export function circleToPolygon(center: LatLng, radiusMeters: number, steps = 64): number[][][] {
  const ring: number[][] = [];
  const latRadius = (radiusMeters / EARTH_RADIUS_M) * (180 / Math.PI);
  const lngRadius = latRadius / Math.cos(rad(center.lat));

  for (let i = 0; i <= steps; i += 1) {
    const theta = (i / steps) * 2 * Math.PI;
    ring.push([center.lng + lngRadius * Math.cos(theta), center.lat + latRadius * Math.sin(theta)]);
  }
  return [ring];
}

export interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export function boundsOf(points: LatLng[]): Bounds | null {
  if (points.length === 0) return null;
  let west = 180;
  let south = 90;
  let east = -180;
  let north = -90;
  for (const { lat, lng } of points) {
    west = Math.min(west, lng);
    east = Math.max(east, lng);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  return { west, south, east, north };
}

/** Shortest-path interpolation across the 359°→1° seam. */
export function lerpAngle(from: number, to: number, t: number): number {
  const delta = ((((to - from) % 360) + 540) % 360) - 180;
  return (from + delta * t + 360) % 360;
}

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}
